// src/pages/SanJuanTourEs.jsx
// Spanish version of San Juan tour
// This imports the Spanish tour data and renders the tour component

import React, { useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import AuthPage from '../AuthPage';

// Extracted imports
import { BRAND, storageKey, finishedKey } from "../utils/tourUtils";
import { useProgress } from "../hooks/useProgress";
import { Button, Card } from "../components/ui";
import { radius } from "../utils/designSystem";
import { MoreMenu, BoxTeaser, UnlockToast, AnswerTrackerModal, ClueCard } from "../components/tour";
import { FinishScreen, WelcomeSplash } from "../components/intro";
import { Modal } from "../components/ui/Modal";

// Spanish tour data
import { sanJuanTourEs } from "../data/tours/sanJuan.es";
import { sanJuanOnboardingCardsEs } from "../data/tours/sanJuanOnboarding.es";
import { sanJuanIntroLetterEs } from "../data/tours/sanJuanIntro.es";

// Spanish UI strings
import esStrings from "../i18n/es.json";

// Placeholder image for when no image is available
const PLACEHOLDER_IMAGE = "https://picsum.photos/1200/800?blur=1";

// Image constants
const STEP1_IMAGE  = "/images/image%201.png";
const STEP2_IMAGE  = "/images/image%202.png";
const STEP3_IMAGE  = "/images/image%203.png";
const STEP4_IMAGE  = "/images/image%204.png";
const STEP5_IMAGE  = "/images/image%205.png";
const STEP6_IMAGE  = "/images/image%206.png";
const STEP7_IMAGE  = "/images/image%207.png";
const STEP8_IMAGE  = "/images/image%208.png";
const STEP9_IMAGE  = "/images/image%209.png";
const STEP10_IMAGE = "/images/image%2010.png";
const STEP11_IMAGE = "/images/image%2011.png";
const STEP12_IMAGE = "/images/image%2012.png";

// Map image paths to tour steps
const stepImages = [STEP1_IMAGE, STEP2_IMAGE, STEP3_IMAGE, STEP4_IMAGE, STEP5_IMAGE, STEP6_IMAGE, STEP7_IMAGE, STEP8_IMAGE, STEP9_IMAGE, STEP10_IMAGE, STEP11_IMAGE, STEP12_IMAGE];

// Prepare Spanish tour data with images
const TIB_SAN_JUAN_ES = {
  ...sanJuanTourEs,
  steps: sanJuanTourEs.steps.map((step, index) => ({
    ...step,
    image: stepImages[index] || step.image,
  })),
};

// Spanish Intro Letter Component
function IntroLetterEs({ slug, onBegin }) {
  const [opened, setOpened] = useState(false);
  const [typingDone, setTypingDone] = useState(false);
  const [skipTyping, setSkipTyping] = useState(false);
  const [twRun, setTwRun] = useState(0);

  const onBeginRef = useRef(onBegin);
  useEffect(() => { onBeginRef.current = onBegin; }, [onBegin]);

  useEffect(() => {
    const finished = localStorage.getItem(finishedKey(slug));
    if (finished === "true") onBeginRef.current?.();
    else {
      setOpened(false);
      setTypingDone(false);
      setSkipTyping(false);
      setTwRun((n) => n + 1);
    }
  }, [slug]);

  const handleOpen = () => {
    if (opened) return;
    setOpened(true);
    setTypingDone(false);
    setSkipTyping(false);
    setTwRun((n) => n + 1);
  };

  const handleBegin = () => {
    onBeginRef.current?.();
  };

  const LETTER_TEXT = sanJuanIntroLetterEs.text;

  // Simple typewriter component
  function Typewriter({ text = "", start = false, speed = 26, skip = false, onDone }) {
    const [count, setCount] = useState(0);
    const done = count >= text.length;

    useEffect(() => {
      if (skip) { setCount(text.length); onDone?.(); return; }
      if (!start || done) return;

      const prev = text[Math.max(0, count - 1)] || "";
      const delay = /[.!?,]/.test(prev) ? speed * 6 : speed;
      const t = setTimeout(() => setCount((c) => Math.min(text.length, c + 1)), delay);
      return () => clearTimeout(t);
    }, [skip, start, done, text, speed, count, onDone]);

    useEffect(() => { if (done) onDone?.(); }, [done, onDone]);

    const shown = text.slice(0, count);
    const parts = shown.split(/\n\n/);
    return (
      <div className="space-y-3" aria-live="polite">
        {parts.map((p, i) => (<p key={i} className="text-slate-800 whitespace-pre-wrap">{p}</p>))}
      </div>
    );
  }

  return (
    <div className="route-fade relative min-h-screen bg-coastal text-slate-900 font-body px-4 py-10">
      <style>{`
        .bg-coastal {
          background: linear-gradient(135deg, #ff9966 0%, #ff5e62 50%, #fcb045 100%);
          background-attachment: fixed;
        }
        .envelope-scene { position: relative; width: 360px; height: 340px; perspective: 1400px; }
        @media (min-width: 420px){ .envelope-scene { width: 400px; height: 360px; } }
        .envelope { position: absolute; inset: 40px 0 0 0; margin: 0 auto; width: 100%; height: 260px; border-radius: 16px; background: linear-gradient(180deg, #f6e5bd, #ecd5a1); box-shadow: 0 10px 30px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.1); border: 1px solid rgba(0,0,0,0.08); transform-style: preserve-3d; transition: transform 600ms ease; z-index: 1; overflow: hidden; }
        .envelope.opened { overflow: visible; }
        .envelope-body { position: absolute; inset: 0; border-radius: 16px; background: linear-gradient(180deg, #f8e9c6, #f2ddb2); z-index: 1; }
        .envelope-flap { position: absolute; top: 0; left: 0; right: 0; height: 58%; background: linear-gradient(180deg, #f0dbac, #e6cd98); clip-path: polygon(0 0, 100% 0, 50% 100%); transform-origin: top center; transition: transform 1100ms cubic-bezier(.2,.7,.2,1); backface-visibility: hidden; z-index: 3; }
        .envelope.opened .envelope-flap { transform: rotateX(182deg); }
        .wax-seal { position: absolute; top: 54%; left: 50%; transform: translate(-50%, -50%); width: 64px; height: 64px; border-radius: 50%; background: #8b1a1a; color: #fff; display: grid; place-items: center; font-weight: 800; letter-spacing: 1px; box-shadow: inset 0 6px 10px rgba(255,255,255,0.15), inset 0 -10px 16px rgba(0,0,0,0.25), 0 6px 18px rgba(0,0,0,0.25); z-index: 4; transition: opacity 600ms ease; }
        .envelope.opened .wax-seal { opacity: 0; }
        .letter { position: absolute; left: 50%; top: -12px; transform: translate(-50%, 0) scale(0.98); opacity: 0; filter: drop-shadow(0 6px 10px rgba(0,0,0,0.22)); transition: opacity 450ms ease, transform 450ms ease; z-index: 5; will-change: transform, opacity; }
        .envelope.opened .letter { opacity: 1; transform: translate(-50%, 0) scale(1); }
        .letter-card { width: 380px; aspect-ratio: 8.5 / 11; height: auto; border-radius: 2px; padding: 24px 28px; background: #fffef9; box-shadow: 0 18px 46px rgba(0,0,0,0.22); border: 1px solid rgba(0,0,0,0.12); text-align: left; font-family: 'Special Elite', 'Courier New', Courier, monospace; }
        @media (max-width: 420px){ .letter-card { width: 340px; } }
        .route-fade { animation: routeFade 420ms cubic-bezier(.2,.7,.2,1); }
        @keyframes routeFade { from { opacity: 0; transform: translateY(8px);} to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div className="mx-auto max-w-md text-center">
        <div className="min-h-[70vh] grid place-items-center">
          {opened && !typingDone && (
            <div className="mb-4 flex items-center justify-center gap-3" style={{ minHeight: '32px' }}>
              <button
                onClick={() => setSkipTyping(true)}
                className="text-sm font-medium text-slate-700 hover:text-slate-900 bg-white border border-slate-300 rounded-lg px-3 py-2"
              >
                {sanJuanIntroLetterEs.skipAnimation}
              </button>
            </div>
          )}
          <div className="envelope-scene">
            <div
              role="button"
              aria-label={sanJuanIntroLetterEs.tapToOpen}
              onClick={handleOpen}
              className={`envelope ${opened ? 'opened' : ''}`}
            >
              <div className="envelope-flap"></div>
              <div className="envelope-body"></div>

              <div className="letter" aria-live="polite">
                <div className="letter-card">
                  <div className="text-center">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{sanJuanIntroLetterEs.fromLine}</div>
                    <div className="mb-2 text-lg font-extrabold">{sanJuanIntroLetterEs.author}</div>
                  </div>

                  {typingDone ? (
                    <div className="space-y-3" aria-live="polite">
                      {LETTER_TEXT.split(/\n\n/).map((p, i) => (
                        <p key={i} className="text-slate-800 whitespace-pre-wrap">{p}</p>
                      ))}
                    </div>
                  ) : (
                    <Typewriter
                      key={`body_${twRun}`}
                      text={LETTER_TEXT}
                      start={opened && !typingDone}
                      speed={64}
                      skip={skipTyping}
                      onDone={() => setTypingDone(true)}
                    />
                  )}

                  <div className="mt-6 flex justify-center">
                    {typingDone && (
                      <button
                        onClick={handleBegin}
                        className="text-center bg-black text-white rounded-full px-6 py-3 font-semibold shadow-sm hover:bg-slate-800 transition-all w-full max-w-xs"
                      >
                        {sanJuanIntroLetterEs.beginButton}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="wax-seal" aria-hidden="true">AR</div>
            </div>

            {!opened && <p className="mt-3 text-slate-600 text-sm">{sanJuanIntroLetterEs.tapToOpen}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// Main AuthedApp component for Spanish tour
function AuthedAppEs({ user }) {
  const tour = useMemo(() => TIB_SAN_JUAN_ES, []);
  const phaseKey = `tib:${tour.slug}:phase`;
  const [phase, setPhase] = useState(() => {
    const saved = localStorage.getItem(phaseKey);
    return saved || "welcome";
  });

  useEffect(() => {
    localStorage.setItem(phaseKey, phase);
  }, [phase, phaseKey]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepAnswered, setStepAnswered] = useState(() =>
    tour.steps.map((_, i) => {
      const key = storageKey(tour.slug, tour.steps[i].id);
      return localStorage.getItem(key) === "true";
    })
  );

  const [showUnlock, setShowUnlock] = useState(null);
  const [showTrackerModal, setShowTrackerModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const {
    progress,
    isRevealed,
    reveal,
    submitAnswer,
    isAnsweredCorrect,
    getLetterMask,
    revealNextLetter
  } = useProgress(tour.slug, tour.steps);
  const totalSteps = tour.steps.length;

  const finishIfDone = (newAnswered) => {
    if (newAnswered.every(Boolean)) {
      localStorage.setItem(finishedKey(tour.slug), "true");
      setPhase("finish");
    }
  };

  const handleRestart = () => {
    // Clear all progress
    localStorage.removeItem(finishedKey(tour.slug));
    localStorage.removeItem(`tib:${tour.slug}:phase`);
    tour.steps.forEach((step) => {
      localStorage.removeItem(storageKey(tour.slug, step.id));
    });
    // Reset state
    setStepAnswered(tour.steps.map(() => false));
    setCurrentStepIndex(0);
    setPhase("welcome");
    window.scrollTo(0, 0);
  };

  const handleAnswer = (stepId, letter) => {
    const idx = tour.steps.findIndex((s) => s.id === stepId);
    if (idx === -1) return;
    setStepAnswered((prev) => {
      const next = [...prev];
      next[idx] = true;
      finishIfDone(next);
      return next;
    });
    localStorage.setItem(storageKey(tour.slug, stepId), "true");
    setShowUnlock(letter);
    setTimeout(() => setShowUnlock(null), 2200);
  };

  const handleStepChange = (newIndex) => {
    if (newIndex >= 0 && newIndex < totalSteps) {
      setCurrentStepIndex(newIndex);
    }
  };

  // Render based on phase
  if (phase === "welcome") {
    return (
      <WelcomeSplash
        onStart={() => setPhase("intro")}
        lang="es"
      />
    );
  }

  if (phase === "onboarding") {
    // Simple onboarding - just show cards and continue
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white p-4">
        <div className="max-w-md mx-auto">
          <div className="space-y-4">
            {sanJuanOnboardingCardsEs.map((card, i) => (
              <Card key={i} className="p-6">
                <h2 className="text-xl font-bold mb-2">{card.title}</h2>
                <p className="text-slate-600 mb-3">{card.body}</p>
                {card.bullets && (
                  <ul className="list-disc list-inside text-sm text-slate-500">
                    {card.bullets.map((b, j) => <li key={j}>{b}</li>)}
                  </ul>
                )}
                {card.steps && (
                  <ol className="list-decimal list-inside text-sm text-slate-500 space-y-1">
                    {card.steps.map((s, j) => <li key={j}>{s}</li>)}
                  </ol>
                )}
              </Card>
            ))}
          </div>
          <Button
            onClick={() => setPhase("intro")}
            className="w-full mt-6"
          >
            {esStrings.onboarding.getStarted}
          </Button>
        </div>
      </div>
    );
  }

  if (phase === "intro") {
    return (
      <IntroLetterEs
        slug={tour.slug}
        onBegin={() => setPhase("tour")}
      />
    );
  }

  if (phase === "finish") {
    return (
      <FinishScreen
        tourSlug={tour.slug}
        onRestart={handleRestart}
        lang="es"
      />
    );
  }

  // Main tour phase
  const currentStep = tour.steps[currentStepIndex];
  const answeredCount = stepAnswered.filter(Boolean).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(1.02);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={BRAND.logoUrl} alt={BRAND.name} className="h-6" />
          </div>
          <div className="flex items-center gap-3">
            <BoxTeaser
              unlocked={stepAnswered.every(Boolean)}
              onOpen={() => {
                localStorage.setItem(finishedKey(tour.slug), "true");
                setPhase("finish");
              }}
            />
            <MoreMenu
              onHelp={() => setShowHelpModal(true)}
              onReset={() => {
                if (window.confirm('¿Reiniciar tu progreso?')) {
                  handleRestart();
                }
              }}
              lang="es"
            />
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="bg-white border-b border-slate-100 px-4 py-2">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span>{esStrings.tour.progress.step} {currentStepIndex + 1} {esStrings.tour.progress.of} {totalSteps}</span>
            <span>{answeredCount} {esStrings.tour.progress.completed}</span>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-300"
              style={{ width: `${(answeredCount / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-2xl mx-auto p-4 pb-28 space-y-6">
        {/* Location image card */}
        <Card padding="none" className="overflow-hidden">
          {currentStep.image && (
            <div className="relative overflow-hidden" style={{ borderRadius: radius.card }}>
              <img
                src={currentStep.image || PLACEHOLDER_IMAGE}
                alt={`Ubicación ${currentStepIndex + 1}`}
                className="h-80 md:h-96 w-full object-cover"
                loading="lazy"
                decoding="async"
                sizes="(max-width: 768px) 100vw, 768px"
                style={{
                  animation: 'fadeIn 0.5s ease-out'
                }}
              />
              {/* Frosted glass overlay card */}
              <div
                className="absolute bottom-0 left-0 right-0 p-5 md:p-6"
                style={{
                  background: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  borderTop: '1px solid rgba(255, 255, 255, 0.3)',
                  borderBottomLeftRadius: radius.card,
                  borderBottomRightRadius: radius.card,
                }}
              >
                <div className="space-y-1">
                  <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                    Ubicación {currentStepIndex + 1} de {totalSteps}
                  </div>
                  <div
                    className="font-libre text-2xl md:text-3xl font-extrabold leading-tight"
                    style={{ color: 'black' }}
                  >
                    {(() => {
                      const t = currentStep.title || '';
                      const i = t.indexOf('–');
                      const lower = t.toLowerCase();
                      return lower.startsWith('pista ') && i > -1
                        ? t.slice(i + 1).trim()
                        : t;
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>

        <ClueCard
          step={currentStep}
          isRevealed={isRevealed}
          reveal={reveal}
          isCorrect={isAnsweredCorrect(currentStep)}
          onSubmit={(val) => {
            submitAnswer(currentStep.id, val);
            handleAnswer(currentStep.id, currentStep.solution?.[0] || '');
          }}
          letterMask={getLetterMask(currentStep)}
          onRevealLetter={() => revealNextLetter(currentStep)}
          lang="es"
          badgeColor="#F8662B"
        />

        {/* Contextual Next Location button - only shows after correct answer */}
        {stepAnswered[currentStepIndex] && currentStepIndex < totalSteps - 1 && (
          <button
            onClick={() => handleStepChange(currentStepIndex + 1)}
            className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 px-6 rounded-xl shadow-lg transition-all duration-200 active:scale-[0.98]"
            style={{
              animation: 'fadeIn 0.3s ease-out'
            }}
          >
            <span>Siguiente Ubicación</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </main>

      {/* Step navigation dots */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3">
        <div className="max-w-2xl mx-auto flex justify-center gap-2 flex-wrap">
          {tour.steps.map((_, i) => (
            <button
              key={i}
              onClick={() => handleStepChange(i)}
              className={`w-8 h-8 rounded-full text-xs font-medium transition-all ${
                i === currentStepIndex
                  ? 'bg-slate-900 text-white'
                  : stepAnswered[i]
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </nav>

      {/* Unlock toast */}
      {showUnlock && (
        <UnlockToast letter={showUnlock} lang="es" />
      )}

      {/* Answer tracker modal */}
      {showTrackerModal && (
        <AnswerTrackerModal
          open={showTrackerModal}
          onClose={() => setShowTrackerModal(false)}
          tour={tour}
          progress={progress}
        />
      )}

      {/* Help modal */}
      <Modal open={showHelpModal} onClose={() => setShowHelpModal(false)} title="Cómo Jugar" showDefaultAction={false}>
        <p className="text-slate-800 mb-3">¡Bienvenido a San Juan! Estas instrucciones explican cómo usar tu tour y puedes volver a verlas en cualquier momento tocando Ayuda.</p>
        <ul className="space-y-3 text-slate-800">
          <li className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: BRAND.accent }}>1</span>
            <div><span className="font-semibold">Comienza en el Puerto de Cruceros</span> – Nuestra aventura comienza en el Puerto de Cruceros del Viejo San Juan, junto al Hotel Rumbao.<a href="https://www.google.com/maps/search/?api=1&query=Hotel%20Rumbao%2C%20San%20Juan%2C%20Puerto%20Rico" target="_blank" rel="noreferrer" className="ml-2 text-sm underline text-slate-700">Ver en Mapa</a></div>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: BRAND.accent }}>2</span>
            <div><span className="font-semibold">Sigue el Camino</span> – Visitarás 12 lugares icónicos en orden, cada uno con direcciones incluidas.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: BRAND.accent }}>3</span>
            <div><span className="font-semibold">Resuelve el Acertijo</span> – En cada parada, abre la Pista de Cofresí, lee el acertijo e ingresa tu respuesta. ¿Necesitas ayuda? Usa el botón de Pista.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: BRAND.accent }}>4</span>
            <div><span className="font-semibold">Explora</span> – Abre "Historia" y "Cerca" para descubrir la historia del lugar y recomendaciones locales.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: BRAND.accent }}>5</span>
            <div><span className="font-semibold">Sigue tu Progreso</span> – Usa los puntos de navegación en la parte inferior para seguir tu progreso. Cuando resuelvas los 12 acertijos, ¡el tesoro final será tuyo!</div>
          </li>
        </ul>
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => setShowHelpModal(false)}
            className="px-4 py-2 text-sm font-semibold rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
          >
            Entendido
          </button>
        </div>
      </Modal>
    </div>
  );
}

// Main export
export default function SanJuanTourEs() {
  const [authStatus, setAuthStatus] = useState("checking");
  const [authUser, setAuthUser] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        setAuthUser(fbUser);
        setAuthStatus("in");
      } else {
        setAuthUser(null);
        setAuthStatus("out");
      }
    });
    return unsub;
  }, []);

  if (authStatus === "checking") {
    return (
      <div className="grid min-h-screen place-items-center">
        <div className="rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-600">
          Verificando tu sesión…
        </div>
      </div>
    );
  }

  if (authStatus === "out") {
    return (
      <div className="min-h-screen p-4">
        <div className="mx-auto w-full max-w-md">
          <AuthPage />
        </div>
      </div>
    );
  }

  return <AuthedAppEs user={authUser} />;
}
